import assert from '@/sui/tools/assert'
import modules from '@/sui/data/modules'
import denominators from '@/sui/data/sheets/edit/denominators'
import toolsUsage from '@/sui/data/sheets/edit/toolsUsage'
import checker from '@/sui/tools/checker'
import rtti from '@/sui/tools/rtti'
import micro from '@/sui/data/microOrgs'

function checkSheetField (i, ch, s, f) {
  assert.notNull(i, `checkSheetField: missing index`)
  assert.notNull(ch, `checkSheetField: missing checker (index ${i})`)
  assert.notNull(s, `checkSheetField: missing context (index ${i})`)

  ch.check(f != null, `${s}: missing value (index ${i})`)
  if (f == null) {
    return
  }

  ch.check(f.field != null, `${s}: missing f.field (index: ${i} title: ${f.config ? f.config.title : ''})`)
  ch.check(f.type != null, `${s}: missing ${f.field}.type`)
  ch.check(f.config != null, `${s}: missing ${f.field}.config`)
  if (f.config == null) {
    return
  }

  const c = f.config
  ch.check(
    c.title != null,
    `${s}: missing ${f.field}.config.title`
  )
  if (f.ignore != null) {
    ch.check(
      rtti.isFunction(f.ignore),
      `${s}: ignore is not a function`
    )
    if (f.selectList != null && f.selectList._list != null) {
      const list = f.selectList._list
      for (let i = 0; i !== list.length; ++i) {
        ch.check(
          rtti.isFunction(list[i]),
          `${s}: ignore.selectList[${i}] is not a function`
        )
      }
    }
  }

  if (f.type === 'array') {
    ch.check(
      c.fields != null,
      `${s}: missing ${f.field}.config.fields`
    )
    if (c.fields == null) {
      return
    }
    ch.check(
      c.fields.length > 0,
      `${s}: ${f.field}.config.fields is empty`
    )
    for (let i in c.fields) {
      const sf = c.fields[i]
      ch.check(sf != null, `${s}: invalid ${f.field}.config.fields[${i}]`)
      if (sf == null) {
        return
      }

      if (i === '0') {
        ch.check(
          sf.pop != null,
          `${s}: missing ${f.field}.config.fields[${i}].pop`
        )
      } else {
        ch.check(
          sf.pop == null,
          `${s}: invalid ${f.field}.config.fields[${i}].pop`
        )
      }
    }
  } else {
    ch.check(c.tooltip != null, `${s}: missing ${f.field}.config.tooltip`)

    if (f.type === 'numeric') {
      ch.check(
        c.digits != null,
        `${s}: missing ${f.field}.config.digits`
      )
    } else if (f.type === 'list') {
      ch.check(
        c.values != null,
        `${s}: missing ${f.field}.config.values`
      )
      if (c.values == null) {
        return
      }
      ch.check(
        c.placeholder != null,
        `${s}: missing ${f.field}.config.placeholder`
      )
      /* ch.check(
        c.optional == null,
        `${s}: ${f.field}.config.optional cannot be defined on a list`
      ) */
      ch.check(
        c.values.length > 0 || f.initialize != null,
        `${s}: list field ${f.field} values empty with no initialize method`
      )
      ch.check(
        c.defaultValue == null ||
          (c.defaultValue >= 0 && c.defaultValue < c.values.length),
        `${s}: list field ${f.field}: defaultValue ${c.defaultValue} is invalid`
      )
      for (let i in c.values) {
        const v = c.values[i]
        ch.check(
          v.code != null,
          `${s}: missing ${f.field}.config.values[${i}].code`
        )
        ch.check(
          v.value != null,
          `${s}: missing ${f.field}.config.values[${i}].value`
        )
      }
    } else if (f.type === 'text') {
      ch.check(
        c.placeholder != null,
        `${s}: missing ${f.field}.config.placeholder`
      )
    }
  }
}

function checkSheet (s, d) {
  assert.notNull(s, `checkSheet: missing scope`)
  assert.notNull(d, `checkSheet: missing sheet (scope ${s})`)

  const ch = checker(`CheckSheet (${s})`)

  ch.check(d.fields != null, `${s}: missing fields`)
  ch.check(d.titles != null, `${s}: missing titles`)
  ch.check(d.buildNotes != null, `${s}: missing buildNotes method`)

  let stack = [ d.fields ]
  let last = null
  while (stack.length > 0) {
    const it = stack[0]
    const fields = []
    for (let i in it) {
      const step = it[i]
      ch.check(
        step != null,
        `${s}: invalid step (${i}${last == null ? '' : ', just after ' + last.field})`
      )
      if (step == null) {
        ch.end()
        return
      }
      checkSheetField(i, ch, s, step)
      fields.push(step.field)

      if (step.type === 'array') {
        stack.push(step.config.fields)
      }
      last = step
    }

    for (let i in fields) {
      const field = fields[i]
      ch.check(
        String(fields.indexOf(field)) === i,
        `${s}: duplicate field ${field}`
      )
    }
    stack.shift()
  }
  ch.end()
}

function checkConvert (scope, convert, sheet) {
  assert.notNull(scope, `checkConvert: missing scope`)
  assert.notNull(convert, `checkConvert: missing values (scope ${scope})`)
  assert.notNull(sheet, `checkConvert: missing sheet (scope ${scope})`)
  const ch = checker(`CheckConvert (${scope})`)
  const savedFields = new Set()

  {
    let stack = [{ scope: `${scope}`, values: convert, prefix: `${scope}` }]
    while (stack.length > 0) {
      const it = stack[0]
      const sc = it.scope
      for (let i in it.values) {
        const v = it.values[i]
        ch.check(v.field != null, `${sc}: missing field for item ${i}`)

        if (v.object == null) {
          const field = v.cfield ? v.cfield : v.field
          savedFields.add(`${it.prefix}/${field}`)
        }

        if (v.array != null) {
          const field = v.cfield ? v.cfield : v.field
          stack.push({
            scope: `${sc}[${i}]/${v.field}`,
            values: v.array,
            prefix: `${it.prefix}/${field}`
          })
        } else if (v.object != null) {
          const prefix = it.prefix // (it.prefix === scope) ? it.prefix : `${it.prefix}/${v.field}`
          stack.push({
            scope: `${sc}[${i}]/${v.field}`,
            values: v.object,
            prefix: prefix
          })
        }
      }
      stack.shift()
    }
  }

  const specialFields = [
    {
      field: 'antibio',
      test: (f) => micro.resistances.v2.findIndex(r => r.field === f) !== -1
    },
    {
      field: 'mainSeverity',
      test: (f) => ['igsII', 'pimII', 'cribII'].indexOf(f) !== -1
    },
    {
      field: 'otherSeverity',
      test: (f) => ['severityType', 'severityScore'].indexOf(f) !== -1
    }
  ]
  const specialFieldsFound = new Set()
  {
    let stack = [{ scope: `${scope}`, values: sheet.fields, prefix: `${scope}` }]
    while (stack.length > 0) {
      const it = stack[0]
      for (let i in it.values) {
        const v = it.values[i]
        let field = v.field

        for (let j = 0; j !== specialFields.length; ++j) {
          const special = specialFields[j]
          if (special.test(field)) {
            const key = `${it.prefix}/${special.field}`
            if (specialFieldsFound.has(key)) {
              field = null
            } else {
              specialFieldsFound.add(key)
              field = special.field
            }
            break
          }
        }

        if (field != null && !savedFields.delete(`${it.prefix}/${field}`)) {
          ch.errors.push(`Field ${it.prefix}/${field} should be saved.`)
        }
        if (v.type === 'array') {
          stack.push({
            scope: `${it.scope}[${i}]/${v.field}`,
            values: v.config.fields,
            prefix: `${it.prefix}/${v.field}`
          })
        } else if (v.type === 'object') {
          const prefix = (it.prefix === scope) ? it.prefix : `${it.prefix}/${v.field}`
          stack.push({
            scope: `${it.scope}[${i}]/${v.field}`,
            values: v.object,
            prefix: prefix
          })
        }
      }

      stack.shift()
    }

    const debugFieldsSavedInExcess = false
    if (debugFieldsSavedInExcess) {
      savedFields.forEach(
        (field) => ch.errors.push(`Saved field ${field} doesn't exist in the sheet.`)
      )
    }
  }
  ch.end()
}

function diffBetweenFields (
  ch, first, second, configValues, prefix = ``, ref = 'field'
) {
  const field = first[ref]

  if (first.type === 'array' && second.type === 'array') {
    ch.errors.push(`------ BEGIN ARRAY ${field} -----`)
    diffBetweenFieldLists(
      ch, first.config.fields, second.config.fields, configValues
    )
    ch.errors.push(`------ END ARRAY ${field} -----`)
    return
  }

  let index = ch.errors.length
  if (ch.checkObjects(
    first, second,
    `${prefix}UPDATE    ${first[ref]} (${first.type})`.padEnd(40) + first[ref]
  )) {
    ch.errors.push(`${prefix}          ${first[ref]} (${first.type})`)
    return
  }

  for (; index !== ch.errors.length; ++index) {
    const last = ch.errors[index]
    if (last.indexOf('config>values: different sizes') !== -1) {
      configValues.push({ first, second })
      break
    }
  }
}

function oneSidedDiff (ch, index, list, otherList, command, move, ref = 'field') {
  let i = index
  while (i !== list.length) {
    const item = list[i]
    const otherIndex = otherList.findIndex((c) => c[ref] === item[ref])
    if (otherIndex === -1) {
      ch.errors.push(`//${command}${item[ref]} (${item.type})`)
    } else {
      ch.errors.push(`// ${move}           ${item[ref]} (${item.type})`)
    }
    ++i
  }
}

function diffBetweenFieldLists (ch, first, second, configValues, ref = 'field') {
  let j = 0
  let i = 0
  while (i !== first.length &&
         j !== second.length) {
    const f = first[i]
    const s = second[j]
    if (f[ref] === s[ref]) {
      diffBetweenFields(ch, f, s, configValues, '//     ', ref)

      ++j
      ++i
      continue
    }

    const findex = first.findIndex((c) => c[ref] === s[ref])
    const sindex = second.findIndex((c) => c[ref] === f[ref])
    if (findex === -1) {
      ch.errors.push(`// +++ ADD       ${s[ref]} (${s.type})`)
      ++j
    }

    if (sindex === -1) {
      ch.errors.push(`// --- REMOVE    ${f[ref]} (${f.type})`)
      ++i
    }

    if (findex === -1 || sindex === -1) {
      continue
    }

    if (sindex !== j - 1) {
      ch.errors.push(`// <<< CUT       ${f[ref]} (${f.type})`)
    }
    ++i

    const prefix = findex !== i ? '// >>> ' : '//    '
    diffBetweenFields(ch, s, first[findex], configValues, prefix, ref)
    ++j
  }

  oneSidedDiff(ch, i, first, second, ' --- REMOVE   ', '<<<', ref)
  oneSidedDiff(ch, j, second, first, ' +++ ADD      ', '<<<', ref)
}

function diffBetweenSortedConfigValues (ch, first, second) {
  let fi = 0
  let si = 0

  while (fi !== first.length && si !== second.length) {
    const fv = first[fi]
    const sv = second[si]

    if (fv.code === sv.code) {
      if (fv.value === sv.value) {
        ch.errors.push(`//              ${fv.code}\t${fv.value}`)
      } else {
        ch.errors.push(`//    UPDATE    ${fv.code}\t${fv.value}\t${sv.value}`)
      }
      ++fi
      ++si
      continue
    }

    if (fv.code < sv.code) {
      ch.errors.push(`// --- REMOVE    ${fv.code}\t${fv.value}`)
      ++fi
      continue
    }

    ch.errors.push(`// +++ ADD      ${sv.code}\t${sv.value}`)
    ++si
  }

  while (fi !== first.length) {
    const fv = first[fi]
    ch.errors.push(`// --- REMOVE    ${fv.code}\t${fv.value}`)
    ++fi
  }

  while (si !== second.length) {
    const sv = second[si]
    ch.errors.push(`// +++ ADD      ${sv.code}\t${sv.value}`)
    ++si
  }
}

function diffBetweenConfigValues (ch, list) {
  for (let i = 0; i !== list.length; ++i) {
    const items = list[i]
    ch.errors.push('')
    ch.errors.push(`DIFF ${items.first.field}.config.values`)
    ch.errors.push('--------------------------')

    const f = items.first.config.values.slice().sort(
      (a, b) => a.code > b.code
    )
    const s = items.second.config.values.slice().sort(
      (a, b) => a.code > b.code
    )

    diffBetweenSortedConfigValues(ch, f, s)
    ch.errors.push('--------------------------')
  }
}

function diffBetweenEdits (ch, first, second) {
  const configValues = []
  ch.errors.push('--------------------------')
  diffBetweenFieldLists(ch, first, second, configValues)
  ch.errors.push('--------------------------')
  diffBetweenConfigValues(ch, configValues)
}

function diffBetweenSheets (module, older, newer) {
  const first = modules[older][module].sheets.edit
  const second = modules[newer][module].sheets.edit
  const ch = checker(`DIFF ${module} ${older} ${newer}`)
  diffBetweenEdits(ch, first.fields, second.fields)
  ch.end()
}

// TODO améliorer avec les schemas
export default function () {
  const years = [
    '2019',
    '2020',
    '2021',
    '2022',
    '2023',
    '2024',
    '2025',
    '2026'
  ]
  years.forEach(
    y => {
      if (denominators[y] != null) {
        checkSheet('denominators', denominators[y])
      }
      if (toolsUsage[y] != null) {
        checkSheet('toolsUsage', toolsUsage[y])
      }

      const list = modules[y]
      for (let m in list) {
        const mod = list[m]
        const sheets = mod.sheets
        if (sheets == null) {
          continue
        }

        const scope = `${m}.${y}`
        if (sheets.edit != null) {
          checkSheet(scope, sheets.edit)
        }

        if (sheets.convert != null && mod.label !== 'BADICAUSE') {
          checkConvert(scope, sheets.convert, sheets.edit)
        }
      }
    }
  )

  const showDiffs = false
  if (showDiffs) {
    diffBetweenSheets('observa4', '2025', '2026')
  }
}
